/**
 * Shop-floor kiosk terminal auth: reads/writes the terminal's capability
 * token via the device-credential storage capability and, while a token is
 * present, sends it as an `X-Kiosk-Terminal-Token` header on every API call
 * through the shared extra-headers provider.
 *
 * Unlike wallboard/site-production-board (token in the URL, re-sent per
 * request as an explicit DataAdapter arg), the kiosk token is provisioned
 * once out-of-band per physical terminal and never appears in a URL — see
 * docs/features/shopfloor-barcode-scanning/03_module_spec_shopfloor_kiosk.md
 * §9's header-vs-query-param rationale. That makes this closer in shape to
 * the app's own Bearer-token auth (an ambient provider, invisible to
 * individual DataAdapter method signatures) than to the wallboard pattern.
 *
 * This hook is the single owner of the kiosk terminal's header registration
 * — the shopfloor-kiosk route tree never mounts alongside the dashboard
 * shell's own extra-headers owner (useScopeOverride), so there's no
 * clobbering risk the way scopeOverride.ts's multi-instance listener set
 * guards against.
 */
import { useCallback, useEffect, useState } from "react";

import { usePlatformCapabilities } from "../capabilities/context";
// Imported directly from ./providers (not ./client) for the same reason
// scopeOverride.ts does — avoids pulling client.ts's module-load read of
// VITE_API_BASE_URL into the mock-mode build's import graph.
import { setExtraHeadersProvider } from "../data/api/providers";

export const KIOSK_TERMINAL_TOKEN_STORAGE_KEY = "cis.kiosk_terminal_token";
export const KIOSK_TERMINAL_TOKEN_HEADER = "X-Kiosk-Terminal-Token";

interface UseKioskAuthResult {
  /** The terminal's stored capability token, or null if this browser has never been set up. */
  terminalToken: string | null;
  /** Persist a newly-entered token (terminal setup) and start sending it on every kiosk API call. */
  setTerminalToken: (token: string) => void;
  /** Forget the stored token — e.g. after the backend reports it unauthorised — returning the kiosk to the "terminal not set up" state. */
  clearTerminalToken: () => void;
}

export function useKioskAuth(): UseKioskAuthResult {
  const capabilities = usePlatformCapabilities();

  const [terminalToken, setTerminalTokenState] = useState<string | null>(() =>
    capabilities.getDeviceCredential(KIOSK_TERMINAL_TOKEN_STORAGE_KEY),
  );

  useEffect(() => {
    setExtraHeadersProvider((): Record<string, string> =>
      terminalToken ? { [KIOSK_TERMINAL_TOKEN_HEADER]: terminalToken } : {},
    );
    return () => {
      setExtraHeadersProvider(() => ({}));
    };
  }, [terminalToken]);

  const setTerminalToken = useCallback(
    (token: string) => {
      capabilities.setDeviceCredential(KIOSK_TERMINAL_TOKEN_STORAGE_KEY, token);
      setTerminalTokenState(token);
    },
    [capabilities],
  );

  const clearTerminalToken = useCallback(() => {
    capabilities.setDeviceCredential(KIOSK_TERMINAL_TOKEN_STORAGE_KEY, null);
    setTerminalTokenState(null);
  }, [capabilities]);

  return { terminalToken, setTerminalToken, clearTerminalToken };
}
